import * as React from 'react'
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import type { AreaDto, AreaListDto } from '@lib/dto/area.dto'
import type { CreateAreaInput, UpdateAreaInput } from '@lib/input/area.input'
import { clientService } from '@/services/ClientService'
import { proposalKeys, areaKeys } from '@/hooks/queryKeys'

/**
 * The areas drawn on one site, oldest first.
 *
 * A draft proposal resolves membership from these rings, so a save also
 * refreshes any open proposal document.
 */
export function useAreas(siteId: string): UseQueryResult<AreaListDto> {
  return useQuery({
    queryKey: areaKeys.list(siteId),
    queryFn: () => clientService.listAreas(siteId),
    enabled: siteId !== '',
    retry: false,
  })
}

function invalidateAreas(queryClient: QueryClient, siteId: string): void {
  void queryClient.invalidateQueries({ queryKey: areaKeys.list(siteId) })
  void queryClient.invalidateQueries({ queryKey: proposalKeys.documents() })
}

/**
 * The site a mutation was started on.
 *
 * TanStack Query replaces a pending mutation's callbacks on re-render, so a
 * closure over `siteId` would refresh whichever site is open when the save
 * finishes. The id is copied into the variables at `mutate`.
 */
function useSiteAtCall(siteId: string): React.RefObject<string> {
  const siteRef = React.useRef(siteId)
  React.useLayoutEffect(() => {
    siteRef.current = siteId
  }, [siteId])
  return siteRef
}

export function useAddArea(siteId: string) {
  const queryClient = useQueryClient()
  const siteRef = useSiteAtCall(siteId)
  const mutation = useMutation<
    AreaDto,
    Error,
    CreateAreaInput & { siteId: string }
  >({
    mutationFn: ({ siteId: savedSite, ...input }) =>
      clientService.addArea(savedSite, input),
    onSuccess: (_area, variables) => {
      invalidateAreas(queryClient, variables.siteId)
    },
  })
  return {
    ...mutation,
    mutate: (input: CreateAreaInput) => {
      mutation.mutate({ ...input, siteId: siteRef.current })
    },
    mutateAsync: (input: CreateAreaInput) =>
      mutation.mutateAsync({ ...input, siteId: siteRef.current }),
  }
}

export function useUpdateArea(siteId: string) {
  const queryClient = useQueryClient()
  const siteRef = useSiteAtCall(siteId)
  const mutation = useMutation<
    AreaDto,
    Error,
    { id: string; input: UpdateAreaInput; siteId: string }
  >({
    mutationFn: ({ id, input }) => clientService.updateArea(id, input),
    onSuccess: (_area, variables) => {
      invalidateAreas(queryClient, variables.siteId)
    },
  })
  return {
    ...mutation,
    mutate: (variables: { id: string; input: UpdateAreaInput }) => {
      mutation.mutate({ ...variables, siteId: siteRef.current })
    },
    mutateAsync: (variables: { id: string; input: UpdateAreaInput }) =>
      mutation.mutateAsync({ ...variables, siteId: siteRef.current }),
  }
}

export function useRemoveArea(siteId: string) {
  const queryClient = useQueryClient()
  const siteRef = useSiteAtCall(siteId)
  const mutation = useMutation<void, Error, { id: string; siteId: string }>({
    mutationFn: ({ id }) => clientService.removeArea(id),
    onSuccess: (_removed, variables) => {
      invalidateAreas(queryClient, variables.siteId)
    },
  })
  return {
    ...mutation,
    mutate: (id: string) => {
      mutation.mutate({ id, siteId: siteRef.current })
    },
    mutateAsync: (id: string) =>
      mutation.mutateAsync({ id, siteId: siteRef.current }),
  }
}
